<script lang="ts" setup>
import logo from '~/assets/images/logo/aquapark-logo.svg'
import aquastakeLogo from '~/assets/images/logo/aquastake-logo.svg'
import aqualendLogo from '~/assets/images/logo/aqualend-logo.svg'
import aquaswapLogo from '~/assets/images/logo/aquaswap-logo.svg'
import { AQUA_LEND_LINK, AQUA_STAKE_LINK, AQUA_SWAP_LINK } from '@/config'

const PROJECTS = [
  {
    name: 'aquastake',
    desc: 'AquaStake is a liquid staking pool that offers users an effortless way to earn rewards while supporting the '
      + 'Waterfall network. By staking WATER tokens, a user receives JWATER, AquaStake’s liquid staking tokens that '
      + 'represent the user’s stake. JWATER can then be used across various DeFi protocols for extra returns. This '
      + 'approach maximizes earning potential, as users benefit from both the passive rewards for staking and the added '
      + 'opportunities available through the AquaPark ecosystem.',
    logo: aquastakeLogo,
    link: AQUA_STAKE_LINK,
    btnLabel: 'STAKE WATER'
  },
  {
    name: 'aquawwap',
    desc: 'AquaSwap is a decentralized exchange that applies advanced automated market maker (AMM) strategies to deliver '
      + 'cost-effective trading. By leveraging a low-slippage technology, AquaSwap minimizes price impact on swaps to ensure a seamless '
      + 'user experience. Liquidity providers can earn rewards by contributing to the platform’s liquidity pools and sharing in trading fees.',
    logo: aquaswapLogo,
    link: AQUA_SWAP_LINK,
    btnLabel: 'SWAP WATER'
  },
  {
    name: 'aqualend',
    desc: 'AquaLend is a lending protocol that provides a secure and transparent environment for both lenders and borrowers. '
      + 'Lenders can earn interest by supplying liquidity, while borrowers can obtain loans by depositing collateral. AquaLend’s '
      + 'smart contracts automatically determine interest rates and execute transactions transparently, giving users an easy way '
      + 'to manage their assets and meet their liquidity needs.',
    logo: aqualendLogo,
    link: AQUA_LEND_LINK
  }
]
</script>

<template>
  <div class="page-wrapper">
    <div class="app-logo">
      <img :src="logo" alt="logo" />
    </div>



    <div class="app-desc">
      AquaPark is a decentralized finance (DeFi) ecosystem that provides users with a range of services for interacting
      with their crypto assets. By integrating multiple DeFi protocols into a single platform, AquaPark offers a
      seamless and secure experience, enabling users to engage with advanced financial strategies:

      <ul>
        <li>
          <strong>AquaStake</strong>: A liquid staking pool that allows users to stake WATER tokens and receive
          JWATER—liquid staking
          tokens that not only represent your stake but also unlock additional opportunities across the ecosystem.
        </li>
        <li>
          <strong>AquaSwap</strong>: A decentralized exchange (DEX) that leverages advanced automated market maker (AMM)
          strategies to facilitate low-slippage trades, ensuring efficient and cost-effective trading while enabling
          liquidity providers to earn rewards from trading fees.
        </li>
        <li>
          <strong>AquaLend</strong>: A lending protocol offering a transparent, algorithm-driven environment where users
          can borrow and lend assets. With collateralized smart contracts managing interest rates and liquidation
          parameters, AquaLend provides a reliable solution for managing liquidity and assets.
        </li>
      </ul>
    </div>

    <div class="projects">
      <div class="projects-project" v-for="project in PROJECTS" :key="project.name">
        <img :src="project.logo" alt="project logo">

        <div class="comming-soon" v-if="!project?.link">
          COMING SOON
        </div>

        <div class="projects-project__desc">
          {{ project.desc }}
        </div>

        <q-btn unelevated v-if="project.link" :href="project.link" target="_blank">
          {{ project?.btnLabel }}
        </q-btn>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.page-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 76px;

  @media (max-width: $breakpoint-sm) {
    padding-top: 50px;
  }


  .app-logo {
    max-width: 490px;
    width: 100%;
    height: auto;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .app-desc {
    color: #FFF;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    padding-top: 44px;

    @media (max-width: $breakpoint-xs) {
      font-size: 18px;
    }

    ul {
      padding-top: 10px;

      li {
        padding-bottom: 20px;
      }
    }
  }

  .projects {
    padding: 96px 0 140px;
    max-width: 630px;
    display: flex;
    flex-direction: column;
    gap: 40px;

    @media (max-width: $breakpoint-xs) {
      padding: 46px 0 80px;
    }

    &-project {
      display: flex;
      padding: 40px 70px 60px 70px;
      flex-direction: column;
      align-items: center;
      gap: 40px;
      border-radius: 10px;
      background: rgba(20, 47, 68, 0.80);

      @media (max-width: $breakpoint-xs) {
        padding: 20px 30px 40px 30px;
      }

      &__desc {
        color: #FFF;
        font-size: 18px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;

        @media (max-width: $breakpoint-xs) {
          font-size: 14px;
        }
      }

      img {
        max-width: 350px;
        width: 100%;
        height: auto;
        object-fit: contain;
      }

      .comming-soon {
        width: 100%;
        height: 64px;
        border-radius: 6px;
        background: $blue-3;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $main;
        font-family: $font-geometos;
        font-size: 32px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        text-transform: uppercase;

        @media (max-width: $breakpoint-xs) {
          font-size: 18px;
        }
      }

      .q-btn {
        width: 100%;
        height: 70px;
        background-color: $main;
        color: #FFF;
        border-radius: 50px;
        font-family: $font-geometos;
        font-size: 30px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;

        @media (max-width: $breakpoint-xs) {
          font-size: 18px;
          height: 50px;
        }
      }
    }
  }
}
</style>